import { useState, useEffect } from "react";
import { Switch } from "antd";
import {
  NotifSection,
  NotifSectionHeader,
  NotifItemRow,
  NotifLabel,
  StyledSwitch,
  ErrorPageContainer,
} from "../StyledComponents";
import MessageToast from "../../../components/Notification/ErrorToast";
import { formatNotificationSettingsLabel } from "../../../utils/formatSnackbarMsg";
import {
  fetchNotificationSettings,
  postNotificationSettings,
} from "../../../service/settings";
import Loader from "../../../components/Loader/Loader";
import { ErrorPageIcon } from "../../../assets/icons";

const NotificationSettings = () => {
  const [notificationPrefs, setNotificationPrefs] = useState({
    signatureRequestSms: false,
    signatureReminderSms: false,
    signatureRequestEmail: false,
    signatureReminderEmail: false,
    externalShareEmail: false,
  });
  const [notificationToast, setNotificationToast] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  const [isLoading, setisLoading] = useState(false);
  const [showErrorPage, setShowErrorPage] = useState(false);

  const handleToggle = async (key) => {
    const updatedValue = !notificationPrefs[key];

    setNotificationPrefs((prev) => ({
      ...prev,
      [key]: updatedValue,
    }));

    try {
      await postNotificationSettings({
        ...notificationPrefs,
        [key]: updatedValue,
      });

      const action = updatedValue ? "enabled" : "disabled";
      handleToastMessage(
        `${formatNotificationSettingsLabel(
          key
        )} has been ${action} successfully.`,
        true
      );
    } catch (err) {
      console.error("Error updating Notification settings: ", err);
      setNotificationPrefs((prev) => ({
        ...prev,
        [key]: !updatedValue,
      }));

      handleToastMessage(
        `Failed to update notification settings for ${formatNotificationSettingsLabel(
          key
        )}.`
      );
    }
  };

  const handleToastMessage = (msg, success = false) => {
    setNotificationToast([
      {
        id: Date.now().toString(),
        newMsg: msg,
        success,
      },
    ]);
    setShowNotification(true);
  };

  useEffect(() => {
    setisLoading(true);
    const fetchData = async () => {
      try {
        const res = await fetchNotificationSettings();
        const data = res?.data?.data;

        const response = {
          signatureRequestSms: data?.sms?.signature_request ?? false,
          signatureReminderSms: data?.sms?.signature_reminder ?? false,
          signatureRequestEmail: data?.email?.signature_request ?? false,
          signatureReminderEmail: data?.email?.signature_reminder ?? false,
          externalShareEmail: data?.email?.external_share ?? false,
        };

        setNotificationPrefs(response);
      } catch (err) {
        console.error("Err fetching Notification settings: ", err);
        handleToastMessage(`Failed to fetch Notification Settings`);
        setShowErrorPage(true);
      } finally {
        setisLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <>
      {showErrorPage ? (
        <ErrorPageContainer>
          <div>
            <ErrorPageIcon />
          </div>
          <div>
            Unable to load data. Please refresh the page or try again later.
          </div>
        </ErrorPageContainer>
      ) : (
        <NotifSection>
          <NotifSectionHeader>SMS</NotifSectionHeader>
          <NotifItemRow>
            <NotifLabel>E-Signature Request SMS</NotifLabel>
            <StyledSwitch checked={notificationPrefs.signatureRequestSms}>
              <Switch
                checked={notificationPrefs.signatureRequestSms}
                onChange={() => handleToggle("signatureRequestSms")}
                disabled
              />
            </StyledSwitch>
          </NotifItemRow>
          <NotifItemRow>
            <NotifLabel>E-Signature Reminder SMS</NotifLabel>
            <StyledSwitch checked={notificationPrefs.signatureReminderSms}>
              <Switch
                checked={notificationPrefs.signatureReminderSms}
                onChange={() => handleToggle("signatureReminderSms")}
                disabled
              />
            </StyledSwitch>
          </NotifItemRow>

          <NotifSectionHeader
            style={{
              border: "1px solid #eaeaea",
            }}
          >
            Emails
          </NotifSectionHeader>
          <NotifItemRow>
            <NotifLabel>E-Signature Request Email</NotifLabel>
            <StyledSwitch checked={notificationPrefs.signatureRequestEmail}>
              <Switch
                checked={notificationPrefs.signatureRequestEmail}
                onChange={() => handleToggle("signatureRequestEmail")}
              />
            </StyledSwitch>
          </NotifItemRow>
          <NotifItemRow>
            <NotifLabel>E-Signature Reminder Email</NotifLabel>
            <StyledSwitch checked={notificationPrefs.signatureReminderEmail}>
              <Switch
                checked={notificationPrefs.signatureReminderEmail}
                onChange={() => handleToggle("signatureReminderEmail")}
              />
            </StyledSwitch>
          </NotifItemRow>
          <NotifItemRow>
            <NotifLabel>External Share Email</NotifLabel>
            <StyledSwitch checked={notificationPrefs.externalShareEmail}>
              <Switch
                checked={notificationPrefs.externalShareEmail}
                onChange={() => handleToggle("externalShareEmail")}
              />
            </StyledSwitch>
          </NotifItemRow>
        </NotifSection>
      )}

      {showNotification && (
        <MessageToast
          message={notificationToast}
          onClose={(id) =>
            setNotificationToast((prev) => {
              const filtered = prev.filter((msg) => msg.id !== id);
              if (filtered.length === 0) setShowNotification(false);
              return filtered;
            })
          }
        />
      )}
      {isLoading && <Loader />}
    </>
  );
};

export default NotificationSettings;
